import { useEffect, useRef } from "react";
import { useAuth, useClerk } from "@clerk/react";

const SESSION_LIMIT_MS = 30 * 60 * 1000; // hard 30-minute session timeout
const IDLE_GRACE_MS = 30 * 1000; // only logout after 30s idle (avoid cutting mid-action)
const ACTIVITY_THROTTLE_MS = 500;
const WINDOW_ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

/**
 * Enforces a hard 30-minute session limit.
 * After the limit, signs out only once the user has been idle for 30 seconds
 * so in-progress actions are not interrupted.
 */
export default function IdleLogout() {
  const { isLoaded, isSignedIn } = useAuth();
  const { signOut } = useClerk();
  const sessionStartedAtRef = useRef<number | null>(null);
  const lastActivityAtRef = useRef(Date.now());
  const checkTimerRef = useRef<number | null>(null);
  const loggingOutRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      sessionStartedAtRef.current = null;
      loggingOutRef.current = false;
      if (checkTimerRef.current) {
        window.clearInterval(checkTimerRef.current);
        checkTimerRef.current = null;
      }
      return;
    }

    if (!sessionStartedAtRef.current) {
      sessionStartedAtRef.current = Date.now();
    }
    lastActivityAtRef.current = Date.now();
    loggingOutRef.current = false;

    const logout = async () => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;
      localStorage.removeItem("accessToken");
      await signOut({ redirectUrl: "/login" });
    };

    const maybeLogout = () => {
      const sessionStartedAt = sessionStartedAtRef.current;
      if (!sessionStartedAt || loggingOutRef.current) return;

      const now = Date.now();
      const sessionExpired = now - sessionStartedAt >= SESSION_LIMIT_MS;
      if (!sessionExpired) return;

      // Session limit hit — wait until the user is idle for 30s
      if (document.visibilityState === "hidden") {
        void logout();
        return;
      }

      const idleFor = now - lastActivityAtRef.current;
      if (idleFor >= IDLE_GRACE_MS) {
        void logout();
      }
    };

    const onActivity = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastActivityAtRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityAtRef.current = now;
    };

    WINDOW_ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", onActivity);

    // Poll so we notice session expiry + idle grace without relying only on events
    checkTimerRef.current = window.setInterval(maybeLogout, 1000);
    maybeLogout();

    return () => {
      if (checkTimerRef.current) {
        window.clearInterval(checkTimerRef.current);
        checkTimerRef.current = null;
      }
      WINDOW_ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity);
      });
      document.removeEventListener("visibilitychange", onActivity);
    };
  }, [isLoaded, isSignedIn, signOut]);

  return null;
}
